import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, map, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const authExpiredInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const isLoginRequest = req.url.includes('/api/sankhya/login');
  const isPublicOrderRoute = router.url.startsWith('/pedidos/');
  const loginExpiredMessage = 'Sua sessao expirou. Faca login novamente.';
  // Chamadas EJB addon podem falhar por permissao/negocio. Redireciona
  // somente quando o retorno indicar sessao/login expirado.
  const isSankhyaRequest = req.url.includes('/api/sankhya/');
  // Chamadas ao Aerosys usam autenticação própria (usuário de serviço, do lado do
  // backend) — um 401/403 ali é um problema de credenciais do Aerosys, nunca da
  // sessão Sankhya do usuário logado. NUNCA deve derrubar a sessão/redirecionar
  // para o login por causa disso.
  const isAerosysRequest = req.url.includes('/api/aerosys/') || req.url.includes('/api/produto-aerosys/');
  // Busca de produto por PN/REFFORN (F3, PN+Enter, importação de Excel e agora
  // importação de itens de O.S) é chamada MUITAS vezes seguidas — inclusive com
  // Part Numbers vindos de outro sistema (Aerosys) que podem não existir no
  // Sankhya. Uma resposta "não encontrado"/negócio aqui NUNCA pode ser
  // interpretada como sessão expirada.
  const isBuscaProdutoRequest = req.url.includes('ProdutoGlobalSP.doBuscarProdutos');
  const ignoraLogoutAutomatico = isAerosysRequest || isBuscaProdutoRequest;
  const redirectToLogin = (): void => {
    authService.clearSession();
    sessionStorage.setItem('authExpiredMessage', loginExpiredMessage);
    if (!router.url.startsWith('/login')) {
      router.navigate(['/login']);
    }
  };

  // A sessao e enviada automaticamente pelo navegador em cookie HttpOnly.
  // Nenhum identificador de autenticacao fica acessivel ao JavaScript.
  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (!isLoginRequest && !ignoraLogoutAutomatico && event instanceof HttpResponse && isDeniedResponse(event.body)) {
        if (!isPublicOrderRoute) {
          redirectToLogin();
        }
        throw new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized',
          url: req.url,
          error: event.body
        });
      }

      return event;
    }),
    catchError((error: unknown) => {
      if (!ignoraLogoutAutomatico && error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        if (isLoginRequest) {
          authService.clearSession();
        } else if (!isPublicOrderRoute && (error.status === 401 || isDeniedResponse(error.error))) {
          // 401 indica autenticação ausente/expirada. 403 também é usado por
          // ações sem permissão (por exemplo, holerites); nesse caso, preserve
          // a sessão. Só redirecione por 403 quando houver indicação de login
          // expirado/inválido no retorno.
          redirectToLogin();
        }
      }

      return throwError(() => error);
    })
  );
};

// Frases especificas de sessao/login realmente expirados ou invalidos — as
// mesmas usadas pelo backend (SankhyaAuthenticationException, ver
// SankhyaGatewayRepository.java) e por respostas nativas do Sankhya nesse
// cenario. NAO usa mais palavras soltas como "usuario"/"login"/"sessao"/
// "senha" isoladas: erros de REGRA DE NEGOCIO legitimos (ex.: "Vendedor nao
// pertence ao usuario logado", "Sessao de caixa nao permite...", validacoes
// do Gerar Venda a Partir de Sistema Legado) citam essas palavras sem terem
// NADA a ver com sessao expirada — e isso estava deslogando o usuario no meio
// de operacoes legitimas (ex.: Salvar um pedido, direto ou pela confirmacao
// de Estoque Insuficiente) sempre que a validacao nativa recusava por outro
// motivo qualquer que citasse uma dessas palavras.
const FRASES_SESSAO_EXPIRADA = [
  // Respostas oficiais documentadas pelo Sankhya (HTTP 401 e 403).
  'nao autenticado',
  'autenticacao invalid',
  // Mensagens especificas emitidas pelo proprio gateway desta aplicacao.
  'sessao expirad',
  'sessao invalid',
  'sessao negada',
  'nao esta autenticado',
  'faca login novamente',
  'login requerido',
  'usuario ou senha invalid',
  'token invalid',
  'token expirad',
];

function isDeniedResponse(body: unknown): boolean {
  if (!body || typeof body !== 'object') {
    return false;
  }

  const response = body as { status?: string; statusMessage?: string; detail?: string };
  const status = response.status;
  const message = normalize(response.statusMessage || response.detail || '');
  const exactMessage = message.replace(/[\s.!:;]+$/g, '').trim();

  return status !== '1' && (
    exactMessage === 'nao autorizado'
    || FRASES_SESSAO_EXPIRADA.some(frase => message.includes(frase))
  );
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
